import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import ReactDOM from "react-dom";
import { Video, Clock, Trash2, Clapperboard, Settings2, MessageCircle, Mic, User, MapPin, Box, Check, Copy, Sparkles, LayoutGrid, Loader2, AtSign } from "lucide-react";
import MultiSelect from "../common/MultiSelect";
import { authenticatedFetch } from "../../../utils/api";
import { API_HOST } from "../../../utils/constants";
import { useTheme } from "../../../context/ThemeContext";
import { useToast } from "../../../context/ToastContext";
import { runTaskToCompletion } from "../../../services/taskService";
import { useDelayedUnmount } from "../../../hooks/useDelayedUnmount";

const ShotControls = ({
  scene,
  index,
  characterOptions,
  sceneOptions,
  propOptions,
  handleChange,
  handleDeleteRow,
  handleCopyPrompt,
  copiedId,
  onOpenAudio,
  onOpenDirectorAdvice,
  projectId,
  episodeId,
}) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();
  const [isGeneratingGridPrompt, setIsGeneratingGridPrompt] = useState(false);

  // @ 多级菜单
  const [atMenu, setAtMenu] = useState(null); // { level, path, category, asset }
  const [atMenuItems, setAtMenuItems] = useState([]);
  const [atMenuLoading, setAtMenuLoading] = useState(false);
  const [atMenuIndex, setAtMenuIndex] = useState(0);

  // 出场动画：@ 菜单延迟卸载（缓存 atMenu 供退场渲染）
  const atMenuAnim = useDelayedUnmount(!!atMenu, 150);
  const lastAtMenuRef = useRef(null);
  if (atMenu) lastAtMenuRef.current = atMenu;
  const atBtnRef = useRef(null);
  const [atHoverPreview, setAtHoverPreview] = useState(null);
  const fieldStyle = {
    backgroundColor: 'var(--color-bg-primary)',
    border: '1px solid var(--color-border-primary)',
    color: 'var(--color-text-primary)',
  };
  const labelStyle = {
    color: 'var(--color-text-muted)',
    letterSpacing: 0,
  };
  const quietButtonStyle = {
    backgroundColor: 'var(--color-bg-tertiary)',
    border: '1px solid var(--color-border-primary)',
    color: 'var(--color-text-secondary)',
  };
  const primaryButtonStyle = {
    backgroundColor: 'var(--color-accent-light)',
    border: '1px solid var(--color-border-hover)',
    color: theme.colors.accentSecondary,
  };
  const directorEnabled = Boolean(scene.director_enabled);
  const directorContext = scene.director_context || {};
  const directorTransitionLabels = {
    OPENING: "起始镜头",
    SAME_SCENE: "同场连续",
    NEW_SCENE: "新场景",
    TIME_JUMP: "时间跳跃",
    INSERT: "插入镜头",
    REACTION: "反应镜头",
    MONTAGE: "蒙太奇",
  };
  const directorSummary = directorContext?.relation_note ||
    directorTransitionLabels[directorContext?.transition_type] ||
    (scene.director_image_prompt ? "已分析" : "待分析");

  const handleToggleDirector = useCallback(() => {
    handleChange(index, "director_enabled", !directorEnabled);
  }, [handleChange, index, directorEnabled]);

  const getAssetTokens = useCallback((asset) => {
    const tokens = [];
    const collect = (value) => {
      if (!value) return;
      if (Array.isArray(value)) {
        value.forEach((item) => collect(item));
        return;
      }
      if (typeof value === 'object') {
        Object.values(value).forEach((item) => collect(item));
        return;
      }
      const raw = String(value).trim();
      if (!raw) return;
      if ((raw.startsWith('[') && raw.endsWith(']')) || (raw.startsWith('{') && raw.endsWith('}'))) {
        try {
          collect(JSON.parse(raw));
          return;
        } catch {
          // Legacy alias strings can fall through to delimiter parsing.
        }
      }
      raw.split(/[,，、\/|｜\s:：;；.。!！?？()（）【】\[\]《》"'“”‘’、\-—_]+/).forEach((part) => {
        const token = part.trim();
        if (token.length >= 2) tokens.push(token);
      });
    };

    collect(asset?.name);
    collect(asset?.aliases);
    return Array.from(new Set(tokens)).sort((a, b) => b.length - a.length);
  }, []);

  const hasUsableReferenceImage = useCallback((asset) => (
    Boolean(asset?.imageUrl) || Boolean((asset?.variations || []).some((v) => v?.imageUrl))
  ), []);

  const getDefaultVariantIndex = useCallback((asset) => {
    if (asset?.imageUrl) return -1;
    const variantIndex = (asset?.variations || []).findIndex((v) => v?.imageUrl);
    return variantIndex >= 0 ? variantIndex : -1;
  }, []);

  const tokenMatchesText = useCallback((token, text) => {
    if (!token || !text) return false;
    const normalizedToken = token.toLowerCase();
    const normalizedText = text.toLowerCase();
    if (/^[a-z0-9_\- ]+$/i.test(token)) {
      const escaped = normalizedToken.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp(`(^|[^a-z0-9_])${escaped}($|[^a-z0-9_])`, 'i').test(normalizedText);
    }
    return normalizedText.includes(normalizedToken);
  }, []);

  const findMatchingAssets = useCallback((options, text) => (
    (options || [])
      .filter(hasUsableReferenceImage)
      .map((asset) => {
        const matchedToken = getAssetTokens(asset).find((token) => tokenMatchesText(token, text));
        return {
          asset,
          score: matchedToken?.length || 0,
          variantIndex: getDefaultVariantIndex(asset),
          matched: Boolean(matchedToken),
        };
      })
      .filter((item) => item.matched)
      .sort((a, b) => b.score - a.score)
  ), [getAssetTokens, getDefaultVariantIndex, hasUsableReferenceImage, tokenMatchesText]);

  const mergeReferenceIds = useCallback((currentIds = [], currentVariantIndexes = [], matches = []) => {
    const nextIds = [...currentIds];
    const nextVariantIndexes = [...currentVariantIndexes];
    let added = 0;

    matches.forEach(({ asset, variantIndex }) => {
      const id = asset.id;
      if (id == null) return;
      const existingIndex = nextIds.findIndex((currentId) => String(currentId) === String(id));
      if (existingIndex >= 0) {
        if (nextVariantIndexes[existingIndex] === undefined) {
          nextVariantIndexes[existingIndex] = variantIndex;
        }
        return;
      }
      nextIds.push(id);
      nextVariantIndexes.push(variantIndex);
      added++;
    });

    return { ids: nextIds, variantIndexes: nextVariantIndexes, added };
  }, []);

  const handleAutoAddReferences = useCallback(() => {
    const sourceText = [
      scene.image_prompt,
      scene.visual_content,
      scene.content,
      scene.action,
      scene.audio_desc,
      scene.location,
    ].filter(Boolean).join('\n');

    if (!sourceText.trim()) {
      toast.warning("请先填写生图提示词或画面描述");
      return;
    }

    const characterMatches = findMatchingAssets(characterOptions, sourceText);
    const sceneMatches = findMatchingAssets(sceneOptions, sourceText);
    const propMatches = findMatchingAssets(propOptions, sourceText);

    const nextCharacters = mergeReferenceIds(scene.refCharacterIds || [], scene.refCharacterVariantIndexes || [], characterMatches);
    const nextScenes = mergeReferenceIds(scene.refSceneIds || [], scene.refSceneVariantIndexes || [], sceneMatches);
    const nextProps = mergeReferenceIds(scene.refPropIds || [], scene.refPropVariantIndexes || [], propMatches);

    if (nextCharacters.added > 0) {
      handleChange(index, "refCharacterIds", nextCharacters.ids);
      handleChange(index, "refCharacterVariantIndexes", nextCharacters.variantIndexes);
    }
    if (nextScenes.added > 0) {
      handleChange(index, "refSceneIds", nextScenes.ids);
      handleChange(index, "refSceneVariantIndexes", nextScenes.variantIndexes);
    }
    if (nextProps.added > 0) {
      handleChange(index, "refPropIds", nextProps.ids);
      handleChange(index, "refPropVariantIndexes", nextProps.variantIndexes);
    }

    const addedTotal = nextCharacters.added + nextScenes.added + nextProps.added;
    if (addedTotal === 0) {
      const matchedTotal = characterMatches.length + sceneMatches.length + propMatches.length;
      toast.info(matchedTotal > 0 ? "匹配到的参考已在当前分镜里" : "没有从文本中匹配到可用参考图");
      return;
    }

    toast.success(`自动添加参考：角色 ${nextCharacters.added}，场景 ${nextScenes.added}，道具 ${nextProps.added}`);
  }, [
    scene,
    index,
    characterOptions,
    sceneOptions,
    propOptions,
    findMatchingAssets,
    mergeReferenceIds,
    handleChange,
    toast,
  ]);

  const atFiltered = useMemo(() => {
    if (!atMenu) return [];
    if (atMenu.level === 'root') {
      return [
        { id: 'character', icon: '🎭', label: '角色工坊', action: 'navigate', nextLevel: 'assets', category: 'character' },
        { id: 'scene', icon: '🏞️', label: '场景工坊', action: 'navigate', nextLevel: 'assets', category: 'scene' },
        { id: 'prop', icon: '🔧', label: '道具工坊', action: 'navigate', nextLevel: 'assets', category: 'prop' },
      ];
    }
    return atMenuItems;
  }, [atMenu, atMenuItems]);

  // ── @ 菜单：选中项预览图同步 ──
  useEffect(() => {
    if (!atMenu || atFiltered.length === 0) { setAtHoverPreview(null); return; }
    const item = atFiltered[atMenuIndex];
    if (item?.imageUrl) {
      setAtHoverPreview({ url: item.imageUrl });
    } else {
      setAtHoverPreview(null);
    }
  }, [atMenuIndex, atMenu, atFiltered]);

  useEffect(() => {
    if (!atMenu) setAtHoverPreview(null);
  }, [atMenu]);

  const handleAtNavigate = useCallback(async (item) => {
    if (!atMenu) return;
    if (item.action !== 'navigate') return;

    const nextLevel = item.nextLevel;
    const nextPath = [...atMenu.path, item.label];
    setAtMenu({ ...atMenu, level: nextLevel, path: nextPath, category: item.category, asset: item.asset });
    setAtMenuIndex(0);
    setAtMenuLoading(true);

    try {
      let items = [];
      if (nextLevel === 'assets') {
        const cat = item.category;
        if (cat === 'character') {
          const data = await authenticatedFetch(`${API_HOST}/api/characters?projectId=${projectId}`);
          items = (data || []).filter(c => c.imageUrl).map(c => ({
            id: c.id, label: c.name, imageUrl: c.imageUrl,
            action: (c.variations?.length > 0) ? 'navigate' : 'select',
            nextLevel: 'variants', category: cat, asset: c, variantIndex: -1,
          }));
        } else if (cat === 'scene') {
          const data = await authenticatedFetch(`${API_HOST}/api/scenes?projectId=${projectId}`);
          items = (data || []).filter(s => s.imageUrl).map(s => ({
            id: s.id, label: s.name, imageUrl: s.imageUrl,
            action: (s.variations?.length > 0) ? 'navigate' : 'select',
            nextLevel: 'variants', category: cat, asset: s, variantIndex: -1,
          }));
        } else if (cat === 'prop') {
          const data = await authenticatedFetch(`${API_HOST}/api/props?projectId=${projectId}`);
          items = (data || []).filter(p => p.imageUrl).map(p => ({
            id: p.id, label: p.name, imageUrl: p.imageUrl,
            action: (p.variations?.length > 0) ? 'navigate' : 'select',
            nextLevel: 'variants', category: cat, asset: p, variantIndex: -1,
          }));
        }
      } else if (nextLevel === 'variants') {
        const asset = item.asset;
        if (asset) {
          items = [{ id: asset.id, label: '主体', imageUrl: asset.imageUrl, action: 'select', category: atMenu.category || item.category, asset, variantIndex: -1 }];
          (asset.variations || []).forEach((v, i) => {
            if (v.imageUrl) {
              items.push({ id: asset.id, label: v.title || `变体${i + 1}`, imageUrl: v.imageUrl, action: 'select', category: atMenu.category || item.category, asset, variantIndex: i });
            }
          });
        }
      }
      setAtMenuItems(items);
    } catch (err) {
      console.error('加载菜单失败:', err);
      setAtMenuItems([]);
    } finally {
      setAtMenuLoading(false);
    }
  }, [atMenu, projectId]);

  // 选中后加入对应的 refIds
  const handleAtSelect = useCallback((item) => {
    if (!item.asset) return;
    const cat = item.category;
    const assetId = item.asset.id;
    const variantIdx = item.variantIndex ?? -1;

    if (cat === 'character') {
      const currentIds = scene.refCharacterIds || [];
      if (!currentIds.includes(assetId)) {
        handleChange(index, 'refCharacterIds', [...currentIds, assetId]);
      }
      if (variantIdx >= 0) {
        const currentVarIdx = scene.refCharacterVariantIndexes || [];
        // 确保数组长度匹配
        const newVarIdx = [...currentVarIdx];
        const idxInList = [...currentIds, assetId].indexOf(assetId);
        newVarIdx[idxInList] = variantIdx;
        handleChange(index, 'refCharacterVariantIndexes', newVarIdx);
      }
    } else if (cat === 'scene') {
      const currentIds = scene.refSceneIds || [];
      if (!currentIds.includes(assetId)) {
        handleChange(index, 'refSceneIds', [...currentIds, assetId]);
      }
      if (variantIdx >= 0) {
        const currentVarIdx = scene.refSceneVariantIndexes || [];
        const newVarIdx = [...currentVarIdx];
        const idxInList = [...currentIds, assetId].indexOf(assetId);
        newVarIdx[idxInList] = variantIdx;
        handleChange(index, 'refSceneVariantIndexes', newVarIdx);
      }
    } else if (cat === 'prop') {
      const currentIds = scene.refPropIds || [];
      if (!currentIds.includes(assetId)) {
        handleChange(index, 'refPropIds', [...currentIds, assetId]);
      }
      if (variantIdx >= 0) {
        const currentVarIdx = scene.refPropVariantIndexes || [];
        const newVarIdx = [...currentVarIdx];
        const idxInList = [...currentIds, assetId].indexOf(assetId);
        newVarIdx[idxInList] = variantIdx;
        handleChange(index, 'refPropVariantIndexes', newVarIdx);
      }
    }

    toast.success(`已添加${item.label}到${cat === 'character' ? '角色' : cat === 'scene' ? '场景' : '道具'}参考`);
    setAtMenu(null);
    setAtMenuItems([]);
  }, [scene, index, handleChange, toast]);

  const handleAtMenuBack = useCallback(() => {
    if (!atMenu || atMenu.path.length === 0) return;
    const newPath = [...atMenu.path];
    newPath.pop();
    if (newPath.length === 0) {
      setAtMenu({ level: 'root', path: [] });
      setAtMenuItems([]);
    } else {
      // 回到 assets 级别需要重新 fetch，简化处理回到 root
      setAtMenu({ level: 'root', path: [] });
      setAtMenuItems([]);
    }
    setAtMenuIndex(0);
  }, [atMenu]);

  const handleGenerateConsistencyPrompt = async () => {
    if (!scene.visual_content && !scene.action) {
      toast.warning("请至少填写【画面描述】或【视频指导】才能生成连贯性提示词");
      return;
    }

    setIsGeneratingGridPrompt(true);

    try {
      const { result } = await runTaskToCompletion("GENERATE_CONSISTENCY_GRID_PROMPT", {
        visual: scene.visual_content || "",
        action: scene.action || "",
        dialogue: scene.content || "",
        audio: scene.audio || ""
      }, { projectId, episodeId, source: "storyboard_consistency_prompt" });
      const gridPrompt = typeof result === "string" ? result : (result?.value || result?.result || "");

      handleChange(index, "image_prompt", gridPrompt);

    } catch (e) {
      console.error(e);
      toast.error("生成失败: " + e.message, 0);
    } finally {
      setIsGeneratingGridPrompt(false);
    }
  };

  return (
    <div className="w-full md:w-[32%] flex flex-col gap-3 min-w-[280px]">
      {/* Header */}
      <div className="flex items-center gap-2 pb-3 border-b" style={{ borderColor: 'var(--color-border-secondary)' }}>
        <div
          className="h-8 px-2.5 rounded-md flex items-center justify-center text-sm font-semibold font-mono min-w-[2.5rem]"
          style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-primary)' }}
        >
          #{scene.shot_number}
        </div>
        <span className="text-2xs font-mono" style={{ color: 'var(--color-text-muted)', opacity: 0.5 }}>id:{scene.id}</span>
        <div className="flex-1 flex items-center gap-1.5 px-2 py-1.5 production-control">
          <Video size={12} style={{ color: 'var(--color-text-muted)' }} />
          <input
            value={scene.shot}
            onChange={(e) => handleChange(index, "shot", e.target.value)}
            className="bg-transparent text-xs w-full outline-none"
            style={{ color: 'var(--color-text-primary)' }}
            placeholder="景别"
          />
        </div>
        <div className="w-20 flex items-center gap-1.5 px-2 py-1.5 production-control">
          <Clock size={12} style={{ color: 'var(--color-text-muted)' }} />
          <input
            value={scene.time || ''}
            onChange={(e) => handleChange(index, "time", e.target.value)}
            className="bg-transparent text-xs w-full outline-none"
            style={{ color: 'var(--color-text-primary)' }}
            placeholder="时长"
          />
        </div>
        <button
          onClick={() => handleDeleteRow(index)}
          className="p-1.5 production-icon-button"
          style={{ color: 'var(--color-text-muted)' }}
          aria-label="删除镜头"
          title="删除镜头"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* 画面描述 */}
      <div className="space-y-1">
        <label className="text-2xs font-semibold flex items-center gap-1 production-section-label" style={labelStyle}>
          <Clapperboard size={10} /> 画面描述
        </label>
        <textarea
          value={scene.visual_content || ""}
          onChange={(e) => handleChange(index, "visual_content", e.target.value)}
          rows={2}
          className="w-full p-2 rounded-md text-xs outline-none resize-none"
          style={fieldStyle}
          placeholder="描述画面..."
        />
      </div>

      {/* 视频指导 */}
      <div className="space-y-1">
        <label className="text-2xs font-semibold flex items-center gap-1 production-section-label" style={labelStyle}>
          <Settings2 size={10} /> 视频指导
        </label>
        <textarea
          value={scene.action || ""}
          onChange={(e) => handleChange(index, "action", e.target.value)}
          rows={2}
          className="w-full p-2 rounded-md text-xs outline-none resize-none"
          style={{ ...fieldStyle, color: 'var(--color-text-secondary)' }}
          placeholder="运镜/光影..."
        />
      </div>

      {/* 对白与配音 */}
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <label className="text-2xs font-semibold flex items-center gap-1 production-section-label" style={labelStyle}>
              <MessageCircle size={10} /> 对白
            </label>
            <button
              onClick={() => onOpenAudio(index, scene.content)}
              className="text-2xs px-1.5 py-0.5 rounded-md transition-all duration-200"
              style={{
                backgroundColor: scene.audio ? 'var(--color-info)' : 'transparent',
                borderColor: scene.audio ? 'var(--color-info)' : 'var(--color-border-primary)',
                color: scene.audio ? 'white' : 'var(--color-text-muted)'
              }}
              onMouseEnter={(e) => { if (!scene.audio) { e.currentTarget.style.borderColor = 'var(--color-info)'; e.currentTarget.style.color = 'var(--color-info)'; }}}
              onMouseLeave={(e) => { if (!scene.audio) { e.currentTarget.style.borderColor = 'var(--color-border-primary)'; e.currentTarget.style.color = 'var(--color-text-muted)'; }}}
            >
              {scene.audio ? "重配" : "配音"}
            </button>
          </div>
          <textarea
            value={scene.content || ""}
            onChange={(e) => handleChange(index, "content", e.target.value)}
            className="w-full p-2 rounded-md text-xs outline-none resize-none"
            style={fieldStyle}
            placeholder="台词..."
            rows={3}
          />
          {scene.audio && scene.audio.startsWith("http") && (
            <audio controls src={scene.audio} className="w-full h-6 rounded" style={{ backgroundColor: 'var(--color-bg-primary)' }} />
          )}
        </div>

        <div className="space-y-1">
          <label className="text-2xs font-semibold flex items-center gap-1 production-section-label" style={labelStyle}>
            <Mic size={10} /> 音效
          </label>
          <textarea
            value={scene.audio_desc || ""}
            onChange={(e) => handleChange(index, "audio_desc", e.target.value)}
            className="w-full p-2 rounded-md text-xs outline-none resize-none"
            style={{ ...fieldStyle, color: 'var(--color-text-secondary)' }}
            placeholder="环境音..."
            rows={3}
          />
        </div>
      </div>

      {/* 资源选择 */}
      <div className="grid grid-cols-3 gap-2">
        <MultiSelect
          label="角色"
          icon={User}
          options={characterOptions}
          selectedIds={scene.refCharacterIds || []}
          onChange={(newIds) => handleChange(index, "refCharacterIds", newIds)}
          variantIndexes={scene.refCharacterVariantIndexes || []}
          onVariantChange={(newIndexes) => handleChange(index, "refCharacterVariantIndexes", newIndexes)}
        />
        <MultiSelect
          label="场景"
          icon={MapPin}
          options={sceneOptions}
          selectedIds={scene.refSceneIds || []}
          onChange={(newIds) => handleChange(index, "refSceneIds", newIds)}
          variantIndexes={scene.refSceneVariantIndexes || []}
          onVariantChange={(newIndexes) => handleChange(index, "refSceneVariantIndexes", newIndexes)}
        />
        <MultiSelect
          label="道具"
          icon={Box}
          options={propOptions}
          selectedIds={scene.refPropIds || []}
          onChange={(newIds) => handleChange(index, "refPropIds", newIds)}
          variantIndexes={scene.refPropVariantIndexes || []}
          onVariantChange={(newIndexes) => handleChange(index, "refPropVariantIndexes", newIndexes)}
        />
      </div>

      {/* 生图指令 */}
      <div className="relative group/prompt flex-1 flex flex-col min-h-[70px]">
        <div className="flex flex-wrap justify-between items-center gap-1 mb-1">
          <label className="text-xs font-semibold flex items-center gap-1 shrink-0" style={{ color: 'var(--color-text-secondary)', letterSpacing: 0 }}>
            <Sparkles size={10} /> 生图提示词
          </label>
          <div className="flex flex-wrap items-center justify-end gap-1">
            <button
              onClick={handleAutoAddReferences}
              className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-md transition-all duration-200"
              style={quietButtonStyle}
              title="根据当前分镜文本自动添加角色、场景、道具参考"
            >
              <Sparkles size={9} />
              <span className="text-2xs">自动添加</span>
            </button>
            <button
              ref={atBtnRef}
              onClick={() => setAtMenu(atMenu ? null : { level: 'root', path: [] })}
              className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-md transition-all duration-200"
              style={atMenu ? primaryButtonStyle : quietButtonStyle}
              title="@ 添加参考资产"
            >
              <AtSign size={9} />
              <span className="text-2xs">参考</span>
            </button>
            <button
              onClick={handleToggleDirector}
              className="flex items-center gap-1 px-2 py-0.5 rounded-md transition-all duration-200 whitespace-nowrap"
              style={directorEnabled ? primaryButtonStyle : quietButtonStyle}
              title="开启后本镜头生成会带上导演上下文"
            >
              <Clapperboard size={8} />
              <span className="text-2xs">导演介入</span>
            </button>
            <button
              onClick={() => onOpenDirectorAdvice?.(index)}
              className="flex items-center gap-1 px-2 py-0.5 rounded-md transition-all duration-200 whitespace-nowrap"
              style={{
                backgroundColor: scene.director_image_prompt ? 'var(--color-purple-light)' : 'var(--color-bg-tertiary)',
                border: '1px solid var(--color-border-primary)',
                color: scene.director_image_prompt ? 'var(--color-purple-primary)' : 'var(--color-text-secondary)'
              }}
              title="读取剧本与前后镜，生成导演建议和提示词"
            >
              <Clapperboard size={8} />
              <span className="text-2xs">导演建议</span>
            </button>
            <button
              onClick={handleGenerateConsistencyPrompt}
              disabled={isGeneratingGridPrompt}
              className="flex items-center gap-1 px-2 py-0.5 rounded-md transition-all duration-200 disabled:opacity-50 whitespace-nowrap"
              style={{ backgroundColor: 'var(--color-blue-light)', border: '1px solid var(--color-border-primary)', color: 'var(--color-blue-primary)' }}
            >
              {isGeneratingGridPrompt ? <Loader2 size={8} className="animate-spin" /> : <LayoutGrid size={8} />}
              <span className="text-2xs">宫格提示词</span>
            </button>
          </div>
        </div>
        {directorEnabled && (
          <div
            className="mb-1 flex items-center justify-between gap-2 rounded-md border px-2 py-1"
            style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-tertiary)' }}
          >
            <div className="min-w-0 flex items-center gap-1.5 text-2xs" style={{ color: 'var(--color-text-secondary)' }}>
              <Clapperboard size={10} style={{ color: theme.colors.accentPrimary }} />
              <span className="shrink-0 font-semibold">导演</span>
              <span className="truncate">{directorSummary}</span>
            </div>
            <button
              onClick={() => onOpenDirectorAdvice?.(index)}
              className="shrink-0 text-2xs px-1.5 py-0.5 rounded-md"
              style={{ color: theme.colors.accentPrimary, backgroundColor: 'var(--color-accent-light)' }}
            >
              {scene.director_image_prompt ? "查看" : "分析"}
            </button>
          </div>
        )}
        <div className="flex-1 relative">
          <textarea
            value={scene.image_prompt}
            onChange={(e) => handleChange(index, "image_prompt", e.target.value)}
            className="w-full h-full min-h-[80px] p-2 rounded-md text-xs font-mono outline-none resize-none"
            style={fieldStyle}
            placeholder="输入生图提示词"
          />
          <button
            onClick={() => handleCopyPrompt(scene.image_prompt, index)}
            className="absolute top-1 right-1 p-1 rounded opacity-0 group-hover/prompt:opacity-100 transition-opacity"
            style={{ backgroundColor: 'var(--color-bg-secondary)', color: 'var(--color-text-muted)' }}
            aria-label="复制提示词"
            title="复制提示词"
          >
            {copiedId === index ? <Check size={10} /> : <Copy size={10} />}
          </button>
        </div>

        {/* @ 多级菜单弹窗 */}
        {atMenuAnim.mounted && (atMenu || lastAtMenuRef.current) && ReactDOM.createPortal(
          <div className="fixed inset-0 z-[300]" tabIndex={-1} onMouseDown={() => { setAtMenu(null); setAtMenuItems([]); }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setAtMenuIndex(p => (p + 1) % Math.max(atFiltered.length, 1)); }
              else if (e.key === 'ArrowUp') { e.preventDefault(); setAtMenuIndex(p => (p - 1 + Math.max(atFiltered.length, 1)) % Math.max(atFiltered.length, 1)); }
              else if (e.key === 'ArrowRight' || e.key === 'Enter') {
                e.preventDefault();
                const item = atFiltered[atMenuIndex];
                if (item?.action === 'select') handleAtSelect(item);
                else if (item) handleAtNavigate(item);
              }
              else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                if ((atMenu || lastAtMenuRef.current)?.path?.length > 0) handleAtMenuBack();
                else { setAtMenu(null); setAtMenuItems([]); }
              }
              else if (e.key === 'Escape') { e.preventDefault(); setAtMenu(null); setAtMenuItems([]); }
            }}
            ref={el => el?.focus()}
          >
            <div
              className={`fixed w-56 rounded-lg shadow-2xl py-1.5 px-1 max-h-64 overflow-y-auto ${atMenuAnim.exiting ? "animate-out fade-out zoom-out-95 duration-150" : "animate-in fade-in zoom-in-95 duration-150"}`}
              style={{
                top: (atBtnRef.current?.getBoundingClientRect().bottom || 0) + 4,
                left: (atBtnRef.current?.getBoundingClientRect().left || 0),
                backgroundColor: 'color-mix(in srgb, var(--color-bg-secondary) 92%, transparent)',
                backdropFilter: 'blur(20px) saturate(1.5)',
                WebkitBackdropFilter: 'blur(20px) saturate(1.5)',
                border: '1px solid var(--color-border-primary)',
              }}
              onMouseDown={e => e.stopPropagation()}
            >
              {(atMenu || lastAtMenuRef.current)?.path?.length > 0 && (
                <div className="flex items-center gap-1 px-2.5 py-1 mb-1" style={{ borderBottom: '1px solid var(--color-border-secondary)' }}>
                  <button onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleAtMenuBack(); }} className="text-2xs hover:underline" style={{ color: 'var(--color-text-muted)' }}>←</button>
                  {(atMenu || lastAtMenuRef.current).path.map((p, i) => (
                    <span key={i} className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>{i > 0 && ' › '}{p}</span>
                  ))}
                </div>
              )}

              {atMenuLoading && (
                <div className="flex items-center justify-center py-3">
                  <Loader2 size={14} className="animate-spin" style={{ color: 'var(--color-text-muted)' }} />
                </div>
              )}

              {!atMenuLoading && atFiltered.map((item, i) => (
                <button
                  key={`${item.id}-${item.label}`}
                  onMouseDown={(e) => {
                    e.preventDefault(); e.stopPropagation();
                    setAtHoverPreview(null);
                    if (item.action === 'select') handleAtSelect(item);
                    else handleAtNavigate(item);
                  }}
                  onMouseEnter={() => setAtMenuIndex(i)}
                  className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-2xs text-left transition-all"
                  style={{
                    color: i === atMenuIndex ? theme.colors.accentPrimary : 'var(--color-text-secondary)',
                    backgroundColor: i === atMenuIndex ? `${theme.colors.accentPrimary}18` : 'transparent',
                  }}
                >
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt="" className="w-6 h-6 rounded object-cover flex-shrink-0" style={{ border: '1px solid var(--color-border-primary)' }} />
                  ) : item.icon ? (
                    <span className="w-6 h-6 flex items-center justify-center text-sm flex-shrink-0">{item.icon}</span>
                  ) : null}
                  <span className="truncate flex-1">{item.label}</span>
                  {item.action === 'navigate' && <span style={{ color: 'var(--color-text-muted)' }}>›</span>}
                </button>
              ))}

              {!atMenuLoading && atFiltered.length === 0 && (
                <div className="px-2.5 py-3 text-center text-2xs" style={{ color: 'var(--color-text-muted)' }}>暂无内容</div>
              )}
            </div>
          </div>,
          document.body
        )}

        {/* 选中项大图预览 */}
        {atHoverPreview && atMenu && ReactDOM.createPortal(
          <div className="fixed z-[400] pointer-events-none" style={{
            right: window.innerWidth - (atBtnRef.current?.getBoundingClientRect().left || 0) + 16,
            top: (atBtnRef.current?.getBoundingClientRect().bottom || 0) + 4,
          }}>
            <img src={atHoverPreview.url} alt="" className="rounded-xl shadow-2xl" style={{
              maxWidth: 'min(400px, 30vw)', maxHeight: 'min(400px, 40vh)', width: 'auto', height: 'auto', objectFit: 'contain',
              backgroundColor: 'color-mix(in srgb, var(--color-bg-overlay) 95%, transparent)',
              border: '1px solid var(--color-border-primary)',
              padding: '4px',
            }} />
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default ShotControls;
